<template>
	<PageWrapper>
		<HeroesBusiness
			:hero="hero"
			:tracking-location="BUSINESS_PAGE_LOCATIONS.hero"
		/>
		<VectorsMobileFiber />
		<VectorsAnimatedFiber :length="1000" />
		<SectionContentContainer classes="-mt-16 pb-12 lg:pb-16">
			<template #items>
				<LazySectionContentItem
					v-for="(item, index) in $tm('business.section-items')"
					:key="item"
					:copy="item"
					:viewbox="BUSINESS_SECTION_ITEMS[index]?.viewbox"
					:image="BUSINESS_SECTION_ITEMS[index]?.image"
					:lines="BUSINESS_SECTION_ITEMS[index]?.lines"
					:circles="BUSINESS_SECTION_ITEMS[index]?.circles"
					:lazy="index > 0"
					hydrate-on-visible
				/>
			</template>
		</SectionContentContainer>
		<LazyPageDivider />
		<LazyBusinessForm :tracking-location="BUSINESS_PAGE_LOCATIONS.contactForm" />
	</PageWrapper>
</template>

<script setup lang="ts">
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import { BUSINESS_SECTION_ITEMS } from '@/constants/components/business'
import { BUSINESS_PAGE_LOCATIONS } from '@/constants/ga4/tracking-ids/pages/business'
import {
	BASE_URL,
	COMPANY_NAME,
	COMPANY_PHONE
} from '@/constants/index.js'
import {
	JSON_LD_LOGO,
	JSON_LD_ORGANIZATION_ADDRESS,
	JSON_LD_ORGANIZATION_AREA_SERVED,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_SAME_AS
} from '@/constants/json-ld'
import { OG_IMAGE_BUSINESS } from '@/constants/og-image'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { t } = useI18n({
	useScope: 'global'
})
const { getStory } = useStoryblokWithErrorReporting()
const route = useRoute()

useHead({
	title: t('business.meta-title'),
	meta: [
		{ name: 'description',
			content: t('business.meta-description', { COMPANY_NAME }) },
		{ name: 'url',
			content: `${BASE_URL}/business-internet` }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

const storyblok = await getStory('business-internet')
const { story } = storyblok.value?.data || {}

const hero = computed(() => {
	return story?.content?.hero?.[0]
})

// Safe access to metatags with fallbacks
const {
	title = '',
	description = ''
} = story?.content?.metatags || {}

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/og-image-fallback.png',
	twitterCard: () => 'summary_large_image'
})

 import.meta.prerender && defineOgImage('Default', OG_IMAGE_BUSINESS)
useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			'itemListElement': [
				{
					'@type': 'ListItem',
					'position': 1,
					'name': 'Home',
					'item': BASE_URL
				}, {
					'@type': 'ListItem',
					'position': 2,
					'name': 'Business Internet',
					'item': `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Service',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'provider': {
				'@type': 'Organization',
				'name': COMPANY_NAME,
				'telephone': COMPANY_PHONE
			},
			'serviceType': 'High-speed Business Internet',
			'serviceOutput': 'Internet service',
			'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED
		},
		{
			'@type': 'Organization',
			'name': COMPANY_NAME,
			'logo': JSON_LD_LOGO,
			'url': BASE_URL,
			'telephone': COMPANY_PHONE,
			'description': t('meta-description', { COMPANY_NAME }),
			'contactPoint': JSON_LD_ORGANIZATION_CONTACT_POINT,
			'sameAs': JSON_LD_ORGANIZATION_SAME_AS,
			'address': JSON_LD_ORGANIZATION_ADDRESS,
			'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED
		}
	]
})
</script>
